/**
 * Shamir secret sharing over the ristretto255 scalar field.
 * Shares are evaluated at x = index (1..n). Threshold k means any k shares reconstruct.
 */
import { ORDER, randomScalar, scalar, scalarFromBytes, scalarToBytes } from "./group.js";
export function assertThreshold(k, n) {
    if (!Number.isInteger(k) || !Number.isInteger(n))
        throw new RangeError("k and n must be integers");
    if (n < 1 || n > 255)
        throw new RangeError("n must be in 1..255");
    if (k < 1 || k > n)
        throw new RangeError("k must be in 1..n");
}
function evalPoly(coeffs, x) {
    // Horner's rule, highest degree first.
    let acc = 0n;
    for (let i = coeffs.length - 1; i >= 0; i--) {
        acc = scalar.add(scalar.mul(acc, x), coeffs[i]);
    }
    return acc;
}
/** Split a secret scalar into n shares with threshold k. Indices are 1..n unless given. */
export function splitScalar(secret, k, n, indices) {
    assertThreshold(k, n);
    const xs = indices ?? Array.from({ length: n }, (_, i) => i + 1);
    if (xs.length !== n)
        throw new RangeError("indices length must equal n");
    if (new Set(xs).size !== n || xs.some((x) => !Number.isInteger(x) || x < 1 || x > 255)) {
        throw new RangeError("indices must be distinct integers in 1..255");
    }
    const coeffs = [scalar.create(secret)];
    for (let i = 1; i < k; i++)
        coeffs.push(randomScalar());
    return xs.map((x) => ({ index: x, value: scalarToBytes(evalPoly(coeffs, BigInt(x))) }));
}
export function split(secret, k, n, indices) {
    return splitScalar(scalarFromBytes(secret), k, n, indices);
}
/**
 * Lagrange coefficient λ_i for reconstructing f(0) from the given set of indices.
 * Returns a map index -> λ.
 */
export function lagrangeAtZero(indices) {
    if (new Set(indices).size !== indices.length)
        throw new RangeError("duplicate share indices");
    const out = new Map();
    for (const i of indices) {
        let num = 1n;
        let den = 1n;
        const xi = BigInt(i);
        for (const j of indices) {
            if (j === i)
                continue;
            const xj = BigInt(j);
            num = scalar.mul(num, xj);
            den = scalar.mul(den, scalar.sub(xj, xi));
        }
        out.set(i, scalar.mul(num, scalar.inv(den)));
    }
    return out;
}
/**
 * Reconstruct the secret from at least k shares. Used for tests and for the pending-vault
 * path before sealing; the media unlock path never calls this (see threshold.ts).
 */
export function combine(shares) {
    if (shares.length === 0)
        throw new RangeError("no shares");
    const lambda = lagrangeAtZero(shares.map((s) => s.index));
    let acc = 0n;
    for (const s of shares) {
        acc = scalar.add(acc, scalar.mul(lambda.get(s.index), scalarFromBytes(s.value)));
    }
    return scalarToBytes(acc);
}
/**
 * Proactive re-sharing, step 1 (run on each of k old confidants' devices):
 * split my share into sub-shares for the new confidant set. Returns one sub-share per new index.
 */
export function reshareSubShares(myShare, newK, newIndices) {
    const subShares = splitScalar(scalarFromBytes(myShare.value), newK, newIndices.length, newIndices);
    return { fromIndex: myShare.index, subShares };
}
/**
 * Proactive re-sharing, step 2 (run on each new confidant's device):
 * combine the sub-shares received from the k old confidants into my new share.
 * All contributions must come from the same set of old indices and target my index.
 */
export function reshareCombine(myNewIndex, contributions) {
    if (contributions.length === 0)
        throw new RangeError("no contributions");
    for (const c of contributions) {
        if (c.subShare.index !== myNewIndex)
            throw new RangeError("sub-share index mismatch");
    }
    const lambda = lagrangeAtZero(contributions.map((c) => c.fromIndex));
    let acc = 0n;
    for (const c of contributions) {
        acc = scalar.add(acc, scalar.mul(lambda.get(c.fromIndex), scalarFromBytes(c.subShare.value)));
    }
    return { index: myNewIndex, value: scalarToBytes(acc) };
}
export { ORDER };
//# sourceMappingURL=shamir.js.map